// ABOUTME: Provides typed same-origin API access and readable request failures.
// ABOUTME: Shared public contracts keep browser state aligned with Worker operations.
import type { ProviderDescriptor } from "../server/providers/contracts";
import type { SharedShortlist } from "../server/modules/projects/sharing";
import type { InspirationBrief } from "../shared/inspiration";
import type {
  ApiErrorBody,
  BriefVersion,
  Candidate,
  CatalogIdea,
  NamingBrief,
  Project,
  Usage,
  AcceptedJob,
  Job,
} from "../shared/contracts";

export type Session = {
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
    emailVerified?: boolean;
  };
  session: { id: string; createdAt?: string };
};
export type PublicConfig = {
  environment?: string;
  brief_review_enabled?: boolean;
  auth_providers?: {
    id: string;
    label?: string;
    enabled: boolean;
    reason?: string;
  }[];
  providers?: { id: string; enabled: boolean; reason?: string }[];
  allowances?: { generation?: number; new_checks?: number };
  sources?: ProviderDescriptor[];
  generation_enabled?: boolean;
  billing_enabled?: boolean;
  local_email_auth?: boolean;
  email_auth?: {
    enabled: boolean;
    mode: "evaluation" | "local" | null;
    invitation_required: boolean;
    email_verified: false;
    verification_available?: boolean;
    password_recovery_available?: boolean;
  };
  turnstile?: { enabled: boolean; required?: boolean; site_key: string | null };
  generation?: { enabled: boolean; model: string };
  mcp?: { resource: string; enabled: boolean };
  [key: string]: unknown;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: "same-origin",
      ...init,
      headers: { "Content-Type": "application/json", ...init.headers },
    });
  } catch {
    throw new ApiError(
      0,
      "network_error",
      "We could not reach NovaName. Check your connection and try again.",
    );
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (data as ApiErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? "request_failed",
      error?.message ??
        `The request did not complete (${response.status}). Please try again.`,
      error?.details,
    );
  }
  return data as T;
}

export type SavedInspirationBrief = {
  id: string;
  brief: InspirationBrief;
  created_at: string;
};
export const api = {
  inspirationBriefs: () =>
    request<{ briefs: SavedInspirationBrief[] }>("/api/v1/inspiration-briefs"),
  createInspirationBrief: (brief: InspirationBrief) =>
    request<SavedInspirationBrief>("/api/v1/inspiration-briefs", {
      method: "POST",
      body: JSON.stringify(brief),
    }),
  removeInspirationBrief: (id: string) =>
    request<{ removed: boolean }>(
      `/api/v1/inspiration-briefs/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),
  config: () => request<PublicConfig>("/api/v1/config"),
  session: () => request<Session | null>("/api/auth/get-session"),
  projects: () => request<{ projects: Project[] }>("/api/v1/projects"),
  createProject: (name: string) =>
    request<Project>("/api/v1/projects", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  brief: (id: string) =>
    request<BriefVersion>(
      `/api/v1/projects/${encodeURIComponent(id)}/briefs/latest`,
    ),
  saveBrief: (id: string, brief: NamingBrief) =>
    request<BriefVersion>(`/api/v1/projects/${encodeURIComponent(id)}/briefs`, {
      method: "POST",
      body: JSON.stringify(brief),
    }),
  candidates: (id: string, cursor?: string) =>
    request<{ candidates: Candidate[]; next_cursor: string | null }>(
      `/api/v1/projects/${encodeURIComponent(id)}/candidates?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  decision: (
    candidate: Candidate,
    decision: Candidate["decision"],
    note?: string,
  ) =>
    request<Candidate>(
      `/api/v1/projects/${encodeURIComponent(candidate.project_id)}/candidates/${encodeURIComponent(candidate.candidate_id)}/decision`,
      {
        method: "PATCH",
        body: JSON.stringify({
          decision,
          revision: candidate.revision,
          ...(note === undefined ? {} : { note }),
        }),
      },
    ),
  saveIdea: (id: string, idea_id: string) =>
    request<Candidate>(
      `/api/v1/projects/${encodeURIComponent(id)}/candidates`,
      { method: "POST", body: JSON.stringify({ idea_id }) },
    ),
  addName: (id: string, name: string) =>
    request<Candidate>(
      `/api/v1/projects/${encodeURIComponent(id)}/candidates`,
      { method: "POST", body: JSON.stringify({ name }) },
    ),
  discover: (project_id: string, brief_version_id: string) =>
    request<{ discovery_id: string; ideas: CatalogIdea[]; charge_units: 0 }>(
      "/api/v1/discoveries",
      {
        method: "POST",
        body: JSON.stringify({ project_id, brief_version_id }),
      },
    ),
  generate: (
    project_id: string,
    brief_version_id: string,
    count = 20,
    idempotency_key: string = operationKey(),
  ) =>
    request<AcceptedJob>("/api/v1/generations", {
      method: "POST",
      body: JSON.stringify({
        project_id,
        brief_version_id,
        count,
        idempotency_key,
      }),
    }),
  job: (id: string) => request<Job>(`/api/v1/jobs/${encodeURIComponent(id)}`),
  usage: () => request<Usage>("/api/v1/usage"),
  shareLink: (id: string) =>
    request<{ link: { created_at: string } | null }>(
      `/api/v1/projects/${encodeURIComponent(id)}/share-link`,
    ),
  makeShareLink: (id: string) =>
    request<{ url: string; created_at: string }>(
      `/api/v1/projects/${encodeURIComponent(id)}/share-link`,
      { method: "POST" },
    ),
  stopSharing: (id: string) =>
    request<{ stopped: boolean }>(
      `/api/v1/projects/${encodeURIComponent(id)}/share-link`,
      { method: "DELETE" },
    ),
  sharedShortlist: (token: string) =>
    request<SharedShortlist>(`/api/shared/${encodeURIComponent(token)}`),
};

export function operationKey() {
  return crypto.randomUUID();
}
export function message(error: unknown) {
  if (
    error instanceof ApiError &&
    error.code === "daily_work_limit" &&
    error.details &&
    typeof error.details === "object"
  ) {
    const details = error.details as { resets_at?: unknown };
    if (typeof details.resets_at === "string") {
      return `${error.message} Resets ${localDate(details.resets_at, true, "UTC")}.`;
    }
  }
  if (
    error instanceof ApiError &&
    error.code === "product_rate_limit" &&
    error.details &&
    typeof error.details === "object"
  ) {
    const { kind, retry_after_seconds: seconds } = error.details as {
      kind?: unknown;
      retry_after_seconds?: unknown;
    };
    if (typeof seconds === "number") {
      const wait = `Try again in ${seconds} second${seconds === 1 ? "" : "s"}.`;
      return kind === "brief_review"
        ? `Briefs can be checked six times a minute. ${wait} Your changes are kept.`
        : `Too many requests in a short time. ${wait}`;
    }
  }
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}
export function localDate(
  value?: string | null,
  precise = false,
  timeZone?: string,
) {
  if (!value) return "Not observed";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(
    undefined,
    precise
      ? {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short",
          timeZone,
        }
      : { dateStyle: "medium", timeZone },
  ).format(date);
}
// Day and minute in the reader's time zone, with the year only outside the current one.
export function localDateTime(value: string, time = true) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    ...(date.getFullYear() === new Date().getFullYear()
      ? {}
      : { year: "numeric" }),
    month: "short",
    day: "numeric",
    ...(time ? { hour: "numeric", minute: "2-digit" } : {}),
  }).format(date);
}
export function localNumber(value: number) {
  return new Intl.NumberFormat().format(value);
}
export function safeLink(value: unknown) {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

export function authorizationRedirect(value: unknown) {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const url = new URL(value, location.origin);
    const nativeScheme = /^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+:$/i.test(
      url.protocol,
    );
    return [
      "https:",
      "http:",
      "vscode:",
      "vscode-insiders:",
      "cursor:",
    ].includes(url.protocol) || nativeScheme
      ? url.href
      : null;
  } catch {
    return null;
  }
}
